import {
  Button,
  Text,
  Flex,
  Input,
  Box,
  DatePicker,
  InputMask,
  MaskEnum,
  Tooltip,
} from '@workwhile/ui'
import {
  useFormContext,
  useFieldArray,
  useController,
  useFormState,
} from 'react-hook-form'
import React, { useEffect } from 'react'
import { set, format, parse, isValid } from 'date-fns'
import { Info, PlusIcon } from 'lucide-react'
import styles from './ShiftsForm.module.css'
import { CompanySelector } from './CompanySelector'
import { PositionSelector } from './PositionSelector'
import {
  parseBulkCreateShiftDataFromClipboard,
  parseBulkCreateShiftDataFromCSVFile,
} from 'pages/Shifts/BulkCreateShifts/utils/parseBulkCreateShiftData'
import {
  ShiftsTableSchema,
  createEmptyShiftRow,
} from '../../../bulkCreateShiftsSchema'
import { ErrorMessage } from './ErrorMessage'
import { WorkerIdsSelector } from './WorkerIdsSelector'
import { v4 } from 'uuid'

const ShiftRow = ({
  index,
  rowId,
  onRemove,
  onDuplicate,
}: {
  index: number
  rowId: string
  onRemove: (index: number) => void
  onDuplicate: (index: number) => void
}) => {
  const { control } = useFormContext<ShiftsTableSchema>()

  const { field: companyIdField } = useController({
    control,
    name: `shifts.${index}.companyId`,
  })

  const { errors } = useFormState({ control })

  const rootError = errors.root?.[rowId]?.message

  const { field: locationNameField, fieldState: locationNameFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationName`,
    })

  const {
    field: locationArrivalInstructionsField,
    fieldState: locationArrivalInstructionsFieldState,
  } = useController({
    control,
    name: `shifts.${index}.locationArrivalInstructions`,
  })

  const { field: locationStreetField, fieldState: locationStreetFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationStreet`,
    })

  const { field: locationUnitField, fieldState: locationUnitFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationUnit`,
    })

  const { field: locationCityField, fieldState: locationCityFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationCity`,
    })

  const { field: locationStateField, fieldState: locationStateFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationState`,
    })

  const { field: locationZipField, fieldState: locationZipFieldState } =
    useController({
      control,
      name: `shifts.${index}.locationZip`,
    })

  const { field: shiftStartDateField, fieldState: shiftStartDateFieldState } =
    useController({
      control,
      name: `shifts.${index}.shiftStartDate`,
    })

  const { field: shiftEndDateField, fieldState: shiftEndDateFieldState } =
    useController({
      control,
      name: `shifts.${index}.shiftEndDate`,
    })

  const {
    field: shiftWorkersNeededField,
    fieldState: shiftWorkersNeededFieldState,
  } = useController({
    control,
    name: `shifts.${index}.shiftWorkersNeeded`,
  })

  const {
    field: shiftLunchLengthField,
    fieldState: shiftLunchLengthFieldState,
  } = useController({
    control,
    name: `shifts.${index}.shiftLunchLength`,
  })

  const { field: shiftPayRateField, fieldState: shiftPayRateFieldState } =
    useController({
      control,
      name: `shifts.${index}.shiftPayRate`,
    })

  const { field: shiftPayLumpSumField, fieldState: shiftPayLumpSumFieldState } =
    useController({
      control,
      name: `shifts.${index}.shiftPayLumpSum`,
    })

  const {
    field: shiftBundleGroupField,
    fieldState: shiftBundleGroupFieldState,
  } = useController({
    control,
    name: `shifts.${index}.shiftBundleGroup`,
  })

  const {
    field: shiftSupervisorNameField,
    fieldState: shiftSupervisorNameFieldState,
  } = useController({
    control,
    name: `shifts.${index}.shiftSupervisorName`,
  })

  const {
    field: shiftSupervisorPhoneField,
    fieldState: shiftSupervisorPhoneFieldState,
  } = useController({
    control,
    name: `shifts.${index}.shiftSupervisorPhone`,
  })

  return (
    <>
      <tr>
        <td
          valign="baseline"
          align="center"
          rowSpan={rootError ? 2 : undefined}
          className={styles.rowNumberColumn}
        >
          <Box lineHeight="40px">{index + 1}</Box>
        </td>
        <td valign="top">
          <CompanySelector fieldName={`shifts.${index}.companyId`} />
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationNameField}
              placeholder="Location Name"
              style={{ width: '200px' }}
            />
            <ErrorMessage error={locationNameFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationArrivalInstructionsField}
              placeholder="Location Arrival Instructions"
              style={{ width: '200px' }}
            />
            <ErrorMessage
              error={locationArrivalInstructionsFieldState.error?.message}
            />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max="10000"
              {...locationStreetField}
              placeholder="Location Street"
              style={{ width: '200px' }}
            />
            <ErrorMessage error={locationStreetFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationUnitField}
              placeholder="Location Unit"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={locationUnitFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationCityField}
              placeholder="Location City"
              style={{ width: '200px' }}
            />
            <ErrorMessage error={locationCityFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationStateField}
              placeholder="Location State"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={locationStateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...locationZipField}
              placeholder="Location Zip"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={locationZipFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <PositionSelector
            fieldName={`shifts.${index}.positionId`}
            companyId={companyIdField.value}
          />
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Box className={styles.datePickerWrapper}>
              <DatePicker
                placeholder="Shift Start Date"
                mode="single"
                selected={
                  isValid(shiftStartDateField.value)
                    ? shiftStartDateField.value
                    : undefined
                }
                onSelect={(date) => {
                  if (date && date instanceof Date) {
                    const currentValue = shiftStartDateField.value
                    const newDate = set(currentValue, {
                      year: date.getFullYear(),
                      month: date.getMonth(),
                      date: date.getDate(),
                    })
                    shiftStartDateField.onChange(newDate)
                  }
                }}
              />
            </Box>
            <ErrorMessage error={shiftStartDateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <InputMask
              mask={MaskEnum.Time}
              value={
                shiftStartDateField.value instanceof Date &&
                isValid(shiftStartDateField.value)
                  ? format(shiftStartDateField.value, 'HH:mm')
                  : shiftStartDateField.value || '00:00'
              }
              onChange={(value) => {
                const time = parse(value, 'HH:mm', shiftStartDateField.value)
                if (isValid(time)) {
                  shiftStartDateField.onChange(time)
                }
              }}
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftStartDateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Box className={styles.datePickerWrapper}>
              <DatePicker
                placeholder="Shift End Date"
                mode="single"
                selected={
                  isValid(shiftEndDateField.value)
                    ? shiftEndDateField.value
                    : undefined
                }
                onSelect={(date) => {
                  if (date && date instanceof Date) {
                    const currentValue = shiftEndDateField.value
                    const newDate = set(currentValue, {
                      year: date.getFullYear(),
                      month: date.getMonth(),
                      date: date.getDate(),
                    })
                    shiftEndDateField.onChange(newDate)
                  }
                }}
              />
            </Box>
            <ErrorMessage error={shiftEndDateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <InputMask
              mask={MaskEnum.Time}
              value={
                shiftEndDateField.value instanceof Date &&
                isValid(shiftEndDateField.value)
                  ? format(shiftEndDateField.value, 'HH:mm')
                  : shiftEndDateField.value || '00:00'
              }
              onChange={(value) => {
                const time = parse(value, 'HH:mm', shiftEndDateField.value)
                if (isValid(time)) {
                  shiftEndDateField.onChange(time)
                }
              }}
              onBlur={shiftEndDateField.onBlur}
              placeholder="Shift End Time"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftEndDateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftWorkersNeededField}
              placeholder="Shift Workers Needed"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftWorkersNeededFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftLunchLengthField}
              placeholder="Shift Lunch Length"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftLunchLengthFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftPayRateField}
              value={shiftPayRateField.value ?? ''}
              placeholder="Shift Pay Rate"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftPayRateFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftPayLumpSumField}
              value={shiftPayLumpSumField.value ?? ''}
              placeholder="Shift Pay Lump Sum"
              style={{ width: '100px' }}
            />
            <ErrorMessage error={shiftPayLumpSumFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftBundleGroupField}
              value={shiftBundleGroupField.value ?? ''}
              placeholder="Shift Bundle Group"
              style={{ width: '200px' }}
            />
            <ErrorMessage error={shiftBundleGroupFieldState.error?.message} />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <Input
              {...shiftSupervisorNameField}
              value={shiftSupervisorNameField.value ?? ''}
              placeholder="Shift Supervisor Name"
              style={{ width: '200px' }}
            />
            <ErrorMessage
              error={shiftSupervisorNameFieldState.error?.message}
            />
          </Flex>
        </td>
        <td valign="top">
          <Flex flexDirection="column" gap={1}>
            <InputMask
              mask={MaskEnum.PhoneNumberAlternate}
              {...shiftSupervisorPhoneField}
              placeholder="Shift Supervisor Phone"
              style={{ width: '200px' }}
            />
            <ErrorMessage
              error={shiftSupervisorPhoneFieldState.error?.message}
            />
          </Flex>
        </td>
        <td valign="top">
          <WorkerIdsSelector shiftIndex={index} />
        </td>
        <td valign="top" className={styles.actionsColumn}>
          <Flex gap={1} pl={1}>
            <Button onClick={() => onDuplicate(index)} px={2}>
              <Text fontWeight="400">Duplicate</Text>
            </Button>
            <Button onClick={() => onRemove(index)} px={2}>
              <Text fontWeight="400">Remove</Text>
            </Button>
          </Flex>
        </td>
      </tr>
      {rootError ? (
        <tr>
          <td colSpan={22}>
            <Text color="red" py={1}>
              {rootError}
            </Text>
          </td>
        </tr>
      ) : null}
    </>
  )
}

const ShiftsTable = ({
  fields,
  remove,
  duplicate,
}: {
  fields: ReturnType<
    typeof useFieldArray<ShiftsTableSchema, 'shifts', 'rowId'>
  >['fields']
  duplicate: (index: number) => void
  remove: ReturnType<
    typeof useFieldArray<ShiftsTableSchema, 'shifts', 'rowId'>
  >['remove']
}) => {
  return (
    <Box width="100%" overflow="auto" pb={3}>
      <table className={styles.shiftsTable}>
        <thead>
          <tr>
            <th align="center" className={styles.rowNumberColumn}>
              #
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Company</Text>
                <Tooltip content="Required. The company that the shift will be created for">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location Name</Text>
                <Tooltip content="Required. Location name has to be unique for a given company and locations will only be properly re-used if the name and all components of the address match exactly">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location Arrival Instructions</Text>
                <Tooltip content="Optional. Will only be used when creating NEW locations">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location Street</Text>
                <Tooltip content="Required">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location Unit</Text>
                <Tooltip content="Optional">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location City</Text>
                <Tooltip content="Required">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location State</Text>
                <Tooltip content="Required. Must be 2 characters long">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Location Zip</Text>
                <Tooltip content="Required. Format is the short, 5 digit zip code">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Position</Text>
                <Tooltip content="Required">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Start Date</Text>
                <Tooltip content="Required. Local date of the shift. Timezone is automatically inferred from location">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Start Time</Text>
                <Tooltip content="Required. Local time of the shift. Timezone is automatically inferred from location">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift End Date</Text>
                <Tooltip content="Required. Local date of the shift. Timezone is automatically inferred from location">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift End Time</Text>
                <Tooltip content="Required. Local time of the shift. Timezone is automatically inferred from location">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Workers Needed</Text>
                <Tooltip content="Required">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Lunch Length</Text>
                <Tooltip content="Lunch length specified in minutes. 0 is valid">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Pay Rate</Text>
                <Tooltip content="Required if pay lump sum is not provided. Specified in $/hr">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Pay Lump Sum</Text>
                <Tooltip content="Required if pay rate is not provided. Specified in $">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Bundle Group</Text>
                <Tooltip content="Optional. Only specify it if several shifts are in a bundle. Bundle Group can be an arbitrary string and any shifts with the same bundle group will be created as bundled within a listing. Note, bundling must be enabled for the corresponding position or creation will fail">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Supervisor Name</Text>
                <Tooltip content="Optional. Required if supervisor phone is provided">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Shift Supervisor Phone</Text>
                <Tooltip content="Optional">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center">
              <Flex flexDirection="row" alignItems="center" gap={1}>
                <Text>Worker IDs</Text>
                <Tooltip content="Required for past shifts. Must match the number of workers needed">
                  <Info size={16} />
                </Tooltip>
              </Flex>
            </th>
            <th align="center" className={styles.actionsColumn}>
              <Text pl={1}>Actions</Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <ShiftRow
              key={`shift-${field.rowId}`}
              index={index}
              rowId={field.rowId}
              onRemove={remove}
              onDuplicate={duplicate}
            />
          ))}
        </tbody>
      </table>
    </Box>
  )
}

export const ShiftsForm = () => {
  const { control } = useFormContext<ShiftsTableSchema>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'shifts',
  })

  const handleAddRow = () => {
    append(createEmptyShiftRow())
  }

  useEffect(() => {
    const handlePaste = async (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData?.getData('text')
      if (!clipboardData) {
        return
      }

      // Always prevent default to intercept the paste
      event.preventDefault()

      const shifts = await parseBulkCreateShiftDataFromClipboard(clipboardData)

      if (shifts.length > 0) {
        // If shifts were parsed, use them
        append(shifts)
      } else {
        // If no shifts were parsed, restore normal paste behavior
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement
        ) {
          const input = activeElement
          const start = input.selectionStart ?? 0
          const end = input.selectionEnd ?? 0
          const value = input.value
          const newValue =
            value.slice(0, start) + clipboardData + value.slice(end)
          input.value = newValue
          input.setSelectionRange(
            start + clipboardData.length,
            start + clipboardData.length
          )
          // Trigger input event for React to pick up the change
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      }
    }

    window.addEventListener('paste', handlePaste)

    return () => {
      window.removeEventListener('paste', handlePaste)
    }
  }, [append])

  const handleFileChange = () => {
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      const reader = new FileReader()
      reader.onload = async (event) => {
        const text = event.target?.result as string
        const shifts = await parseBulkCreateShiftDataFromCSVFile(text)

        if (shifts.length === 0) {
          return
        }

        append(shifts)
      }
      reader.readAsText(file)
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.csv'
    input.onchange = (event: unknown) =>
      handleFileChange(event as React.ChangeEvent<HTMLInputElement>)
    input.click()
  }

  const handleDuplicate = (index: number) => {
    const shift = fields[index]
    append({
      ...shift,
      rowId: v4(),
    })
  }

  return (
    <Flex
      flexDirection="column"
      gap={3}
      align="center"
      justifyContent={fields.length > 0 ? 'flex-start' : 'center'}
      height="100%"
      minHeight="auto"
    >
      {fields.length > 0 && (
        <ShiftsTable
          fields={fields}
          remove={remove}
          duplicate={handleDuplicate}
        />
      )}
      {fields.length === 0 && (
        <Box textAlign="center">
          <Text c="dimmed" mt="xl">
            Upload a CSV file, copy and paste from a spreadsheet directly to
            this page, or add a shift to get started
          </Text>
        </Box>
      )}

      <Flex gap={2} justifyContent="center" flexShrink={0} pt={2}>
        <Button variant="secondary" onClick={handleFileChange}>
          Upload CSV
        </Button>
        <Button
          leftSection={<PlusIcon size={16} />}
          onClick={handleAddRow}
          variant="secondary"
          fullWidth={false}
          style={{ flexShrink: 0 }}
        >
          Add Shift
        </Button>
      </Flex>
    </Flex>
  )
}
